import { createContext, useContext, useEffect, useMemo, useState } from "react";
import { useViewAmbientContext } from "../AmbientsContext";
import {useViewImageContext} from "../../Images/ViewImageContext";
import {useViewLandContext} from "../../ViewLandContext";
import {
  PLANET_HD_EARLIEST_CAMPAIGN_YEAR,
  getPlanetHdEarliestDate,
} from "../../Images/planetHdAcquisitionQuery";
import {
  REMOTE_SENSING_PRODUCTS,
  normalizeRemoteSensingSource,
} from "../../Images/remoteSensingModel";
import {getAvailablePlanetHdCampaignYears} from "../planetHdCampaignModel";

const AmbientDialogContext = createContext();

export function useAmbientDialogContext() {
  return useContext(AmbientDialogContext);
}

export const AmbientDialogConsumer = AmbientDialogContext.Consumer;

export function AmbientDialogProvider({ children }) {
  const viewAmbient = useViewAmbientContext();
  const viewImage = useViewImageContext();
  const viewLand = useViewLandContext();
  const {ensureLandHdAcquisitions} = viewImage;
  const land = viewLand.land;

  /* ambient dialog form */
  const minimumYear = viewAmbient.ambientDialogMode === "hd"
    ? PLANET_HD_EARLIEST_CAMPAIGN_YEAR
    : 2013;
  const totalYears = useMemo(() => {
    const values = [];
    for (var year = minimumYear; year <= new Date().getFullYear(); year++) {
      values.push(year);
    }
    return values;
  }, [minimumYear]);

  const [countAmbients, setCountAmbients] = useState('5');
  const [season, setSeason] = useState('Estival');
  const [years, setYears] = useState(totalYears);
  const [monthFrom, setMonthFrom] = useState("10");
  const [monthTo, setMonthTo] = useState("05");
  const [disabled, setDisabled] = useState(false);
  const [hdAcquisitions, setHdAcquisitions] = useState({
    items: [],
    status: "idle",
  });
  const availableCampaignYears = useMemo(() => {
    if (viewAmbient.ambientDialogMode !== "hd") {
      return totalYears;
    }

    return getAvailablePlanetHdCampaignYears(
      hdAcquisitions.items,
      monthFrom,
      monthTo
    );
  }, [hdAcquisitions.items, monthFrom, monthTo, totalYears, viewAmbient.ambientDialogMode]);
  const campaignYearsLoading = viewAmbient.ambientDialogMode === "hd" &&
    ["idle", "loading"].includes(hdAcquisitions.status);

  const value = {
    countAmbients, setCountAmbients,
    season, setSeason,
    years, setYears,
    monthFrom, setMonthFrom,
    monthTo, setMonthTo,
    disabled, setDisabled,
    minimumYear,
    availableCampaignYears,
    campaignYearsLoading,
    hdCampaignsStatus: hdAcquisitions.status,
  };

  useEffect(() => {
    if (viewAmbient.ambientDialogMode !== "hd" || !land?.land_id) {
      return undefined;
    }

    let cancelled = false;
    setHdAcquisitions({items: [], status: "loading"});
    const source = normalizeRemoteSensingSource({
      product: REMOTE_SENSING_PRODUCTS.PLANET_SCOPE_HD,
      layer: "visible",
    });

    ensureLandHdAcquisitions(
      land,
      source,
      100,
      {dateFrom: getPlanetHdEarliestDate()}
    ).then((state) => {
      if (!cancelled) {
        setHdAcquisitions({
          items: state?.items || [],
          status: state?.status === "error" ? "error" : "ready",
        });
      }
    }).catch(() => {
      if (!cancelled) {
        setHdAcquisitions({items: [], status: "error"});
      }
    });

    return () => {
      cancelled = true;
    };
  }, [ensureLandHdAcquisitions, land, viewAmbient.ambientDialogMode]);

  useEffect(() => {
    if (viewAmbient.ambientDialogMode !== "hd" || campaignYearsLoading) {
      return;
    }

    setYears((current) => {
      const next = current.filter((year) => availableCampaignYears.includes(year));
      return next.length === current.length && next.every((year, index) => year === current[index])
        ? current
        : next;
    });
  }, [availableCampaignYears, campaignYearsLoading, viewAmbient.ambientDialogMode]);

  useEffect(() => {
    if (viewAmbient.dialogInitialData != null) {
      const initial = viewAmbient.dialogInitialData;
      setCountAmbients(initial['countAmbients']);
      setYears(initial['years']);
      setMonthFrom(initial['monthFrom']);
      setMonthTo(initial['monthTo']);
      viewAmbient.setDialogInitialData(null);
    }
  }, [viewAmbient.dialogInitialData])

  return (
    <>
      <AmbientDialogContext.Provider value={value}>
        {children}
      </AmbientDialogContext.Provider>
    </>
  );
};
